import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ADMIN_API_URL,
  getAdminMe,
  listVendors,
  createVendor,
  updateVendor,
  updateVendorStatus,
  deleteVendor,
  listVendorCategories,
  listVendorExecutives
} from '../api';
import AdminShell from './AdminShell';
import '../styles/AdminDashboard.css';
import '../styles/GlassAdmin.css';

const STATUS_META = {
  verified: { label: 'Verified', icon: 'verified', cls: 'verified' },
  pending: { label: 'Pending', icon: 'pending', cls: 'pending' },
  suspended: { label: 'Suspended', icon: 'block', cls: 'suspended' }
};

const STATUS_TABS = [
  { key: 'All Statuses', label: 'All' },
  { key: 'verified', label: 'Verified' },
  { key: 'pending', label: 'Pending' },
  { key: 'suspended', label: 'Suspended' }
];

const EMPTY_FORM = {
  name: '',
  ownerName: '',
  serviceType: '',
  categoryId: '',
  plan: '',
  salesExecutiveId: '',
  commissionModel: '',
  listingType: 'General Listing',
  isPremium: false
};


function handleCardMouseMove(e) {
  const card = e.currentTarget;
  const rect = card.getBoundingClientRect();
  const mx = ((e.clientX - rect.left) / rect.width) * 100;
  const my = ((e.clientY - rect.top) / rect.height) * 100;
  card.style.setProperty('--mx', `${mx}%`);
  card.style.setProperty('--my', `${my}%`);
}

export default function VendorManagement() {
  const navigate = useNavigate();
  const [admin, setAdmin] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [executives, setExecutives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All Statuses');
  const [categoryFilter, setCategoryFilter] = useState('All Categories');

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const loadVendors = useCallback(() => {
    setLoading(true);
    listVendors({
      status: statusFilter,
      category: categoryFilter,
      search: searchTerm || undefined
    })
      .then(({ data }) => setVendors(data.vendors))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [statusFilter, categoryFilter, searchTerm]);

  useEffect(() => {
    getAdminMe()
      .then(({ data }) => setAdmin(data.admin))
      .catch(() => {
        localStorage.removeItem('adminToken');
        sessionStorage.removeItem('adminToken');
        navigate('/login');
      });

    listVendorCategories()
      .then(({ data }) => setCategories(data.categories))
      .catch(() => {});

    listVendorExecutives()
      .then(({ data }) => setExecutives(data.executives))
      .catch(() => {});
  }, [navigate]);

  useEffect(() => {
    const timeout = setTimeout(loadVendors, 250);
    return () => clearTimeout(timeout);
  }, [loadVendors]);

  const openCreateModal = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormError('');
    setShowModal(true);
  };

  const openEditModal = (vendor) => {
    setEditingId(vendor.id);

    const selectedCategory = categories.find(
      (c) => c.name === vendor.category_name
    );

    setForm({
      name: vendor.name,
      ownerName: vendor.owner_name,
      serviceType: vendor.category_name || '',
      categoryId: selectedCategory?.id || '',
      plan: vendor.plan || '',
      salesExecutiveId:
        executives.find((e) => e.name === vendor.executive_name)?.id || '',
      commissionModel: vendor.commission_model || '',
      listingType: vendor.listing_type || 'General Listing',
      isPremium: !!vendor.is_premium
    });

    setFormError('');
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    if (!form.name.trim() || !form.ownerName.trim()) {
      setFormError('Vendor name and owner name are required.');
      return;
    }

    setSubmitting(true);

    try {
      const args = [
        form.name.trim(),
        form.ownerName.trim(),
        form.serviceType,
        form.categoryId || null,
        form.plan.trim(),
        form.salesExecutiveId || null,
        form.commissionModel.trim(),
        form.listingType,
        form.isPremium
      ];

      if (editingId) {
        await updateVendor(editingId, ...args);
      } else {
        await createVendor(...args);
      }

      setShowModal(false);
      loadVendors();
    } catch (err) {
      setFormError(
        err.response?.data?.message || 'Failed to save vendor.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerify = async (vendor) => {
    try {
      await updateVendorStatus(vendor.id, 'verified');
      loadVendors();
    } catch (err) {
      alert(
        err.response?.data?.message || 'Failed to verify vendor.'
      );
    }
  };

  const handleDelete = async (vendor) => {
    if (!window.confirm(`Delete "${vendor.name}"? This can't be undone.`)) {
      return;
    }

    try {
      await deleteVendor(vendor.id);
      loadVendors();
    } catch (err) {
      alert(
        err.response?.data?.message || 'Failed to delete vendor.'
      );
    }
  };

  const verifiedCount = vendors.filter((v) => v.status === 'verified').length;
  const pendingCount = vendors.filter((v) => v.status === 'pending').length;
  const premiumCount = vendors.filter((v) => v.is_premium).length;

  return (
    <AdminShell
      admin={admin}
      badges={{}}
      pageTitle="Vendor Management"
      searchValue={searchTerm}
      onSearchChange={setSearchTerm}
      searchPlaceholder="Search vendors..."
    >
      <div className="glass-page">
        <div className="dash-welcome-row">
          <div>
            <h2 className="dash-welcome-title">
              Vendor Management
              {pendingCount > 0 && (
                <span className="glass-pill-badge urgent">
                  {pendingCount} pending
                </span>
              )}
            </h2>
            <p className="dash-welcome-subtitle">
              Manage service providers, statuses, and subscriptions.
            </p>
          </div>

          <div className="dash-quick-actions">
            <button
              className="dash-quick-btn primary"
              onClick={openCreateModal}
            >
              <span
                className="material-symbols-outlined"
                style={{ fontSize: 18 }}
              >
                add
              </span>
              Register New Vendor
            </button>
          </div>
        </div>

        <div className="glass-kpi-grid">
  <article className="glass-kpi-card" onMouseMove={handleCardMouseMove}>
    <div className="glass-kpi-top">
      <span>Total Vendors</span>
      <span className="material-symbols-outlined glass-kpi-icon">
        storefront
      </span>
    </div>
    <div className="glass-kpi-value">{vendors.length}</div>
    <div className="glass-kpi-sub">Across all categories</div>
  </article>

  <article className="glass-kpi-card" onMouseMove={handleCardMouseMove}>
    <div className="glass-kpi-top">
      <span>Verified</span>
      <span className="material-symbols-outlined glass-kpi-icon">
        verified
      </span>
    </div>
    <div className="glass-kpi-value">{verifiedCount}</div>
    <div className="glass-kpi-sub">Live and approved</div>
  </article>

  <article className="glass-kpi-card" onMouseMove={handleCardMouseMove}>
    <div className="glass-kpi-top">
      <span>Pending</span>
      {pendingCount > 0 && (
        <span className="glass-kpi-tag urgent">Action needed</span>
      )}
    </div>
    <div className="glass-kpi-value">{pendingCount}</div>
    <div className="glass-kpi-sub">Awaiting verification</div>
  </article>

  <article className="glass-kpi-card" onMouseMove={handleCardMouseMove}>
    <div className="glass-kpi-top">
      <span>Premium</span>
      <span className="material-symbols-outlined glass-kpi-icon">
        star
      </span>
    </div>
    <div className="glass-kpi-value">{premiumCount}</div>
    <div className="glass-kpi-sub">Premium listings</div>
  </article>
</div>

        <div className="dash-filter-row" style={{ marginBottom: 18 }}>
          <div className="glass-tab-row" style={{ marginBottom: 0 }}>
            {STATUS_TABS.map((tab) => (
              <button
                key={tab.key}
                className={`glass-tab ${
                  statusFilter === tab.key ? 'active' : ''
                }`}
                onClick={() => setStatusFilter(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <select
            className="dash-filter-select"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <option>All Categories</option>

            {categories.map((c) => (
              <option key={c.id} value={c.name}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        <div className="glass-directory-panel glass-table-panel">
          <div className="dash-directory-table-wrap">
            {loading ? (
              <div className="dash-empty-state">
                Loading vendors...
              </div>
            ) : vendors.length === 0 ? (
              <div className="dash-empty-state">
                No vendors match your filters.
              </div>
            ) : (
              <table className="dash-directory-table">
                <thead>
                  <tr>
                    <th>Vendor Name</th>
                    <th>Category</th>
                    <th>Owner Name</th>
                    <th>Sales Executive</th>
                    <th>Status</th>
                    <th>Plan</th>
                    <th>Commission Model</th>
                    <th className="right">Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {vendors.map((v) => {
                    const meta =
                      STATUS_META[v.status] || STATUS_META.pending;

                    return (
                      <tr key={v.id}>
                        <td>
                          <div className="glass-vendor-cell">
                            <div className="glass-avatar-tile">
                              {v.logo_initials || v.name[0]}
                            </div>

                            <div>
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 6,
                                  flexWrap: 'wrap'
                                }}
                              >
                                <p className="dash-exec-name">
                                  {v.name}
                                </p>

                                {v.is_premium && (
                                  <span className="dash-premium-badge">
                                    <span
                                      className="material-symbols-outlined"
                                      style={{ fontSize: 12 }}
                                    >
                                      star
                                    </span>
                                    Premium
                                  </span>
                                )}
                              </div>

                              <p className="dash-exec-email">
                                Joined{' '}
                                {v.joined_at
                                  ? new Date(
                                      v.joined_at
                                    ).toLocaleDateString('en-IN', {
                                      month: 'short',
                                      year: 'numeric'
                                    })
                                  : '—'}
                              </p>
                            </div>
                          </div>
                        </td>

                        <td>
                          {v.category_name && (
                            <span className="dash-category-pill">
                              {v.category_icon?.startsWith('/') ||
                              v.category_icon?.startsWith('http') ? (
                                <img
  src={
    v.category_icon.startsWith('http')
      ? v.category_icon
      : `${ADMIN_API_URL.replace('/api/admin', '')}${v.category_icon}`
  }
  alt=""
  style={{
    width: 16,
    height: 16,
    objectFit: 'contain'
  }}
/>
                              ) : (
                                <span
                                  className="material-symbols-outlined"
                                  style={{ fontSize: 16 }}
                                >
                                  {v.category_icon || 'category'}
                                </span>
                              )}

                              {v.category_name}
                            </span>
                          )}
                        </td>

                        <td>{v.owner_name}</td>

                        <td>{v.executive_name || '—'}</td>

                        <td>
                          <span
                            className={`dash-status-pill ${meta.cls}`}
                          >
                            <span
                              className="material-symbols-outlined"
                              style={{
                                fontSize: 14,
                                verticalAlign: 'middle',
                                marginRight: 2
                              }}
                            >
                              {meta.icon}
                            </span>

                            {meta.label}
                          </span>
                        </td>

                        <td>
                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 2
                            }}
                          >
                            <span>{v.plan || '—'}</span>

                            <span
                              style={{
                                fontSize: 10,
                                fontWeight: 700,
                                color: 'var(--on-surface-variant)',
                                textTransform: 'uppercase',
                                letterSpacing: '0.03em'
                              }}
                            >
                              {v.listing_type}
                            </span>
                          </div>
                        </td>

                        <td>{v.commission_model || '—'}</td>

                        <td className="dash-row-actions">
                          {v.status === 'pending' && (
                            <button
                              className="dash-link-btn activate"
                              onClick={() => handleVerify(v)}
                            >
                              Verify
                            </button>
                          )}

                          <button
                            className="dash-link-btn edit"
                            onClick={() => openEditModal(v)}
                          >
                            Edit
                          </button>

                          <button
                            className="dash-link-btn deactivate"
                            onClick={() => handleDelete(v)}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {showModal && (
          <div
            className="dash-modal-backdrop"
            onClick={() => setShowModal(false)}
          >
            <div
              className="dash-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="dash-modal-title">
                {editingId
                  ? 'Edit Vendor'
                  : 'Register New Vendor'}
              </h3>

              {formError && (
                <div className="admin-banner error">
                  {formError}
                </div>
              )}

              <form
                onSubmit={handleSubmit}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 16,
                  maxHeight: '60vh',
                  overflowY: 'auto',
                  paddingRight: 4
                }}
              >
                <div className="dash-modal-field">
                  <label htmlFor="vendorName">
                    Vendor Name
                  </label>

                  <input
                    id="vendorName"
                    type="text"
                    value={form.name}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        name: e.target.value
                      })
                    }
                    placeholder="e.g. Apex Plumbing Pros"
                  />
                </div>

                <div className="dash-modal-field">
                  <label htmlFor="ownerName">
                    Owner Name
                  </label>

                  <input
                    id="ownerName"
                    type="text"
                    value={form.ownerName}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        ownerName: e.target.value
                      })
                    }
                    placeholder="e.g. Michael Chang"
                  />
                </div>

                <div className="dash-modal-field">
                  <label htmlFor="vendorCategory">
                    Category
                  </label>

                  <select
                    id="vendorCategory"
                    className="admin-text-input"
                    value={form.categoryId}
                    onChange={(e) => {
                      const selected = categories.find(
                        (c) =>
                          String(c.id) ===
                          String(e.target.value)
                      );

                      setForm({
                        ...form,
                        categoryId: e.target.value,
                        serviceType: selected?.name || ''
                      });
                    }}
                  >
                    <option value="">
                      Select a category
                    </option>

                    {categories.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="dash-modal-field">
                  <label htmlFor="vendorExecutive">
                    Sales Executive
                  </label>

                  <select
                    id="vendorExecutive"
                    className="admin-text-input"
                    value={form.salesExecutiveId}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        salesExecutiveId: e.target.value
                      })
                    }
                  >
                    <option value="">
                      Unassigned
                    </option>

                    {executives.map((ex) => (
                      <option key={ex.id} value={ex.id}>
                        {ex.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="dash-modal-field">
                  <label htmlFor="vendorPlan">
                    Plan
                  </label>

                  <input
                    id="vendorPlan"
                    type="text"
                    value={form.plan}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        plan: e.target.value
                      })
                    }
                    placeholder="e.g. Monthly Basic"
                  />
                </div>

                <div className="dash-modal-field">
                  <label htmlFor="vendorListingType">
                    Listing Type
                  </label>

                  <select
                    id="vendorListingType"
                    className="admin-text-input"
                    value={form.listingType}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        listingType: e.target.value
                      })
                    }
                  >
                    <option value="General Listing">
                      General Listing
                    </option>

                    <option value="Paid Listing">
                      Paid Listing
                    </option>
                  </select>
                </div>

                <div className="dash-modal-field">
                  <label htmlFor="vendorCommission">
                    Commission Model
                  </label>

                  <input
                    id="vendorCommission"
                    type="text"
                    value={form.commissionModel}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        commissionModel: e.target.value
                      })
                    }
                    placeholder="e.g. 10% Commission or ₹10 / Listing"
                  />
                </div>

                <label
                  className="admin-remember-label"
                  style={{ marginTop: -4 }}
                >
                  <input
                    type="checkbox"
                    checked={form.isPremium}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        isPremium: e.target.checked
                      })
                    }
                  />

                  Mark as Premium vendor
                </label>

                <div className="dash-modal-actions">
                  <button
                    type="button"
                    className="dash-modal-btn secondary"
                    onClick={() => setShowModal(false)}
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    className="dash-modal-btn primary"
                    disabled={submitting}
                  >
                    {submitting
                      ? 'Saving...'
                      : editingId
                      ? 'Save Changes'
                      : 'Register'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AdminShell>
  );
}